import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Search, Swords } from "lucide-react";

import { ListGroup, Sheet } from "@/ui";
import { CreateChallengeDialog } from "@/features/challenges/CreateChallengeDialog";
import { usePlayState, type PlayState } from "./api";
import { describeMode, describeWhen } from "./format";

/** The search's own parameters when one is running, else what the row does. */
function searchSubtitle(state: PlayState | undefined): string {
  const req = state?.request;
  if (req) {
    const where = req.spots.map((s) => s.name).join(", ");
    const head = req.status === "searching" ? "Ищем" : "Нашли! Согласуйте игру";
    return `${head}: ${where} · ${describeWhen(req)} · ${describeMode(req.mode)}`;
  }
  const n = state?.games.length ?? 0;
  if (n > 0) return `Назначено игр: ${n}. Можно начать новый поиск`;
  return "Подберём пару или компанию на споте рядом";
}

/**
 * A found game → straight to its board; a live search or upcoming games →
 * their status screen (it has the «new search» button too); else the form.
 */
function searchTarget(state: PlayState | undefined): string {
  const req = state?.request;
  if (req?.status === "matched" && req.game_id) return `/play/game/${req.game_id}`;
  return req || state?.games.length ? "/play" : "/play/search";
}

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Entry point of the central «Играть» tab-bar button: either hidden
 * matchmaking («Найти соперника») or a direct challenge to a registered
 * player («Вызвать конкретного игрока» — the existing Challenge flow).
 */
export function PlaySheet({ open, onClose }: Props) {
  const [challengeOpen, setChallengeOpen] = useState(false);
  const navigate = useNavigate();
  // Only while the sheet is open — no app-wide polling from the tab bar.
  const state = usePlayState({ enabled: open });

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <>
      <Sheet open={open} onClose={onClose} title="Играть">
        <div style={{ paddingBottom: 8 }}>
          <ListGroup>
            <SheetRow
              icon={<Search size={20} strokeWidth={1.75} />}
              title="Найти соперника"
              subtitle={searchSubtitle(state.data)}
              onClick={() => go(searchTarget(state.data))}
            />
            <SheetRow
              icon={<Swords size={20} strokeWidth={1.75} />}
              title="Вызвать конкретного игрока"
              subtitle="Выбери игрока из зарегистрированных"
              onClick={() => {
                onClose();
                setChallengeOpen(true);
              }}
            />
          </ListGroup>
        </div>
      </Sheet>
      <CreateChallengeDialog open={challengeOpen} onClose={() => setChallengeOpen(false)} />
    </>
  );
}

/**
 * Like ListRow, but title and subtitle wrap instead of being cut with an
 * ellipsis — these labels don't fit one line on a phone.
 */
function SheetRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="sp-list-row"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "12px 16px",
        background: "transparent",
        border: 0,
        textAlign: "left",
        cursor: "pointer",
        fontFamily: "var(--font-body)",
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          flex: "none",
          borderRadius: "50%",
          background: "var(--brand-orange-ghost)",
          color: "var(--brand-orange)",
          display: "grid",
          placeItems: "center",
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 17, fontWeight: 500, lineHeight: "22px", color: "var(--fg1)" }}>
          {title}
        </span>
        <span style={{ display: "block", fontSize: 13, lineHeight: "18px", color: "var(--fg2)", marginTop: 2 }}>
          {subtitle}
        </span>
      </span>
      <ChevronRight size={18} color="var(--fg3)" style={{ flex: "none" }} />
    </button>
  );
}
